import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Container,
  Loader2,
  LogIn,
  LogOut,
  RefreshCw,
  ShieldCheck,
  Truck,
  X,
} from 'lucide-react';
import { API_BASE } from '@/lib/api-base';

type ApiEnvelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

interface PortalUser {
  id: string;
  email: string;
  displayName: string;
  companyName: string;
  role: string;
  tenantId: string;
  tenantName: string;
}

interface PortalSession {
  accessToken: string;
  user: PortalUser;
}

interface StockItem {
  id: string;
  container_no: string;
  container_size_type: string | null;
  load_status: string | null;
  visit_status: string;
  condition_status: string | null;
  hold_status: string | null;
  gate_in_at: string | null;
  line_operator: string | null;
  depot_name: string | null;
  depot_code: string | null;
}

interface AppointmentItem {
  id: string;
  appointment_no: string;
  appointment_type: string;
  status: string;
  container_no: string | null;
  line_operator: string | null;
  container_size_type: string | null;
  truck_plate: string | null;
  driver_name: string | null;
  haulier_name: string | null;
  release_ref: string | null;
  scheduled_start_at: string;
  scheduled_end_at: string;
  arrival_at: string | null;
  departure_at: string | null;
  note: string | null;
  depot_name: string | null;
  depot_code: string | null;
}

interface TimelineEvent {
  event_type: string;
  event_at: string | null;
  description: string;
  actor?: string;
}

interface TimelineData {
  containerNo: string;
  visitStatus: string;
  events: TimelineEvent[];
}

interface RepairEstimate {
  id: string;
  estimate_no: string;
  container_no: string;
  approval_status: string;
  total_amount: string | number;
  currency: string;
  damage_summary: string | null;
  approval_required: boolean;
  approval_requested_at: string | null;
  depot_name: string | null;
  depot_code: string | null;
  visit_status: string | null;
}

const SESSION_KEY = 'boox_depot_portal_session';

const demoAccounts = [
  { label: 'Line Approver', email: 'portal-line@boox.test' },
  { label: 'Customer Viewer', email: 'portal-customer@boox.test' },
  { label: 'Trucker Desk', email: 'portal-trucker@boox.test' },
];

async function depotApi<T>(path: string, token: string | null, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (!payload.ok) {
    throw new Error(payload.error.message || `Request failed with ${response.status}`);
  }
  return payload.data;
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-SG', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusClass(status: string): string {
  if (['approved', 'completed', 'clear', 'in_yard'].includes(status)) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (['pending', 'checked_in', 'on_hold', 'repair_hold', 'approval_pending'].includes(status)) {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (['rejected', 'cancelled', 'no_show'].includes(status)) {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass(status)}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function DepotPortalPage() {
  const [session, setSession] = useState<PortalSession | null>(null);
  const [tenantSlug, setTenantSlug] = useState('revochem');
  const [email, setEmail] = useState('portal-line@boox.test');
  const [password, setPassword] = useState('');
  const [stock, setStock] = useState<StockItem[]>([]);
  const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
  const [repairs, setRepairs] = useState<RepairEstimate[]>([]);
  const [timeline, setTimeline] = useState<TimelineData | null>(null);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visibleContainers = useMemo(() => {
    const values = new Set<string>();
    stock.forEach((item) => values.add(item.container_no));
    appointments.forEach((item) => {
      if (item.container_no) values.add(item.container_no);
    });
    repairs.forEach((item) => values.add(item.container_no));
    return [...values];
  }, [appointments, repairs, stock]);

  async function loadOverview(token = session?.accessToken ?? '', preferredContainer = selectedContainer) {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [stockData, appointmentData, repairData] = await Promise.all([
        depotApi<{ items: StockItem[] }>('/api/depot/portal/stock', token),
        depotApi<{ items: AppointmentItem[] }>('/api/depot/portal/appointments', token),
        depotApi<{ items: RepairEstimate[] }>('/api/depot/portal/repair-estimates', token),
      ]);
      setStock(stockData.items);
      setAppointments(appointmentData.items);
      setRepairs(repairData.items);

      const nextContainer =
        preferredContainer ||
        stockData.items[0]?.container_no ||
        appointmentData.items.find((item) => item.container_no)?.container_no ||
        repairData.items[0]?.container_no ||
        '';
      setSelectedContainer(nextContainer);
      if (nextContainer) {
        const timelineData = await depotApi<TimelineData>(
          `/api/depot/portal/containers/${encodeURIComponent(nextContainer)}/timeline`,
          token,
        );
        setTimeline(timelineData);
      } else {
        setTimeline(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load depot portal');
    } finally {
      setLoading(false);
    }
  }

  async function loadTimeline(containerNo: string) {
    if (!session?.accessToken) return;
    setSelectedContainer(containerNo);
    setError(null);
    try {
      const timelineData = await depotApi<TimelineData>(
        `/api/depot/portal/containers/${encodeURIComponent(containerNo)}/timeline`,
        session.accessToken,
      );
      setTimeline(timelineData);
    } catch (err) {
      setTimeline(null);
      setError(err instanceof Error ? err.message : 'Failed to load timeline');
    }
  }

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const loginData = await depotApi<{ accessToken: string; user: PortalUser }>(
        '/api/depot/portal/auth/login',
        null,
        {
          method: 'POST',
          body: JSON.stringify({ tenantSlug, email, password }),
        },
      );
      const nextSession = { accessToken: loginData.accessToken, user: loginData.user };
      localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
      setSession(nextSession);
      await loadOverview(nextSession.accessToken, '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setStock([]);
    setAppointments([]);
    setRepairs([]);
    setTimeline(null);
    setSelectedContainer('');
  }

  async function decideRepair(estimate: RepairEstimate, decision: 'approve' | 'reject') {
    if (!session?.accessToken) return;
    setActionLoading(`${decision}:${estimate.id}`);
    setError(null);
    try {
      await depotApi<{ id: string; status: string }>(
        `/api/depot/portal/repair-estimates/${estimate.id}/${decision}`,
        session.accessToken,
        {
          method: 'POST',
          body: JSON.stringify({ reason: `${decision} via Revochem UAT portal` }),
        },
      );
      await loadOverview(session.accessToken, estimate.container_no);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${decision} repair estimate`);
    } finally {
      setActionLoading(null);
    }
  }

  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return;
    try {
      const stored = JSON.parse(raw) as PortalSession;
      if (stored?.accessToken && stored.user?.email) {
        setSession(stored);
        void loadOverview(stored.accessToken, '');
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
  }, []);

  return (
    <section className="min-h-[calc(100vh-4rem)] bg-slate-50" data-testid="depot-portal-page">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
              <Container className="h-4 w-4" />
              Depot Portal
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">Revochem Depot UAT Workspace</h1>
          </div>
          {session && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-700">
                {session.user.displayName}
              </span>
              <button
                type="button"
                onClick={() => void loadOverview()}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!session ? (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="grid gap-4 sm:grid-cols-3">
                {demoAccounts.map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    onClick={() => setEmail(account.email)}
                    className={`rounded-lg border px-4 py-4 text-left transition-colors ${
                      email === account.email
                        ? 'border-brand-500 bg-brand-50 text-brand-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-sm font-semibold">{account.label}</div>
                    <div className="mt-1 break-all text-xs text-slate-500">{account.email}</div>
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleLogin} className="rounded-lg border border-slate-200 bg-white p-6" data-testid="depot-portal-login-form">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShieldCheck className="h-4 w-4 text-brand-600" />
                Portal Login
              </div>
              <label className="mb-3 block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Tenant</span>
                <input
                  value={tenantSlug}
                  onChange={(event) => setTenantSlug(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </label>
              <label className="mb-3 block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </label>
              <label className="mb-5 block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                Sign in
              </button>
            </form>
          </div>
        ) : (
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">Visible containers</span>
                    <Container className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">{visibleContainers.length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">Appointments</span>
                    <CalendarClock className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">{appointments.length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">Pending approvals</span>
                    <ClipboardCheck className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">{repairs.length}</div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white" data-testid="depot-portal-stock-panel">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Container className="h-4 w-4 text-brand-600" />
                    Stock
                  </div>
                  {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Container</th>
                        <th className="px-4 py-3">Depot</th>
                        <th className="px-4 py-3">Line</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Hold</th>
                        <th className="px-4 py-3">Gate in</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stock.map((item) => (
                        <tr
                          key={item.id}
                          className={`cursor-pointer hover:bg-slate-50 ${selectedContainer === item.container_no ? 'bg-brand-50/60' : ''}`}
                          onClick={() => void loadTimeline(item.container_no)}
                        >
                          <td className="px-4 py-3 font-semibold text-brand-700">{item.container_no}</td>
                          <td className="px-4 py-3 text-slate-600">{item.depot_code ?? item.depot_name ?? '-'}</td>
                          <td className="px-4 py-3 text-slate-600">{item.line_operator ?? '-'}</td>
                          <td className="px-4 py-3"><StatusPill status={item.visit_status} /></td>
                          <td className="px-4 py-3"><StatusPill status={item.hold_status} /></td>
                          <td className="px-4 py-3 text-slate-500">{formatDateTime(item.gate_in_at)}</td>
                        </tr>
                      ))}
                      {stock.length === 0 && (
                        <tr>
                          <td className="px-4 py-6 text-center text-slate-500" colSpan={6}>
                            No in-yard stock visible for this account.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white" data-testid="depot-portal-appointments-panel">
                <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                  <Truck className="h-4 w-4 text-brand-600" />
                  Appointments / Gate
                </div>
                <div className="divide-y divide-slate-100">
                  {appointments.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => item.container_no && void loadTimeline(item.container_no)}
                      className="flex w-full flex-col gap-2 px-4 py-3 text-left hover:bg-slate-50 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">{item.appointment_no}</span>
                          <StatusPill status={item.status} />
                          <StatusPill status={item.appointment_type} />
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          {item.container_no ?? '-'} · {item.truck_plate ?? '-'} · {item.driver_name ?? item.haulier_name ?? '-'}
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 md:text-right">
                        <div>{formatDateTime(item.scheduled_start_at)}</div>
                        <div>{item.release_ref ?? item.depot_code ?? '-'}</div>
                      </div>
                    </button>
                  ))}
                  {appointments.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">
                      No appointments visible for this account.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-lg border border-slate-200 bg-white" data-testid="depot-portal-lifecycle-panel">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Clock3 className="h-4 w-4 text-brand-600" />
                    Container Lifecycle
                  </div>
                  <select
                    value={selectedContainer}
                    onChange={(event) => void loadTimeline(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-brand-500"
                  >
                    {visibleContainers.map((containerNo) => (
                      <option key={containerNo} value={containerNo}>{containerNo}</option>
                    ))}
                  </select>
                </div>
                <div className="px-4 py-4">
                  {timeline ? (
                    <div>
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-950">{timeline.containerNo}</span>
                        <StatusPill status={timeline.visitStatus} />
                      </div>
                      <div className="space-y-3">
                        {timeline.events.map((event, index) => (
                          <div key={`${event.event_type}-${index}`} className="flex gap-3">
                            <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-brand-600" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-900">{event.description}</div>
                              <div className="mt-0.5 text-xs text-slate-500">
                                {formatDateTime(event.event_at)}{event.actor ? ` · ${event.actor}` : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="py-6 text-center text-sm text-slate-500">
                      Select a visible container to view lifecycle.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white" data-testid="depot-portal-repair-panel">
                <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                  <CircleDollarSign className="h-4 w-4 text-brand-600" />
                  Repair Approval
                </div>
                <div className="divide-y divide-slate-100">
                  {repairs.map((repair) => (
                    <div key={repair.id} className="px-4 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-950">{repair.estimate_no}</span>
                            <StatusPill status={repair.approval_status} />
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {repair.container_no} · {repair.damage_summary ?? 'Repair estimate'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-slate-950">
                            {repair.currency} {Number(repair.total_amount).toFixed(2)}
                          </div>
                          <div className="text-xs text-slate-500">{formatDateTime(repair.approval_requested_at)}</div>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void decideRepair(repair, 'approve')}
                          disabled={actionLoading !== null}
                          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {actionLoading === `approve:${repair.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => void decideRepair(repair, 'reject')}
                          disabled={actionLoading !== null}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {actionLoading === `reject:${repair.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                  {repairs.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">
                      No repair approvals visible for this account.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
